import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  WorkflowOfficeConfig,
  WorkflowState,
  ActionType,
} from '../office-assignment/office-assignment-config/office-assignment.model';

@Component({
  selector: 'irembogov-workflow-state-selector',
  templateUrl: './workflow-state-selector.component.html',
  styleUrls: ['./workflow-state-selector.component.scss'],
})
export class WorkflowStateSelectorComponent implements OnInit, OnDestroy {
  @Input() workflowConfig!: WorkflowOfficeConfig;
  @Input() initialState?: WorkflowState;
  @Output() stateSelected = new EventEmitter<WorkflowState>();

  stateForm: FormGroup;
  availableStartStates: string[] = [];
  availableEvents: string[] = [];
  availableEndStates: Array<{
    type: 'endStateOne' | 'endStateTwo';
    code: string;
  }> = [];
  actionTypes: ActionType[] = [
    'breakingAction',
    'breakingActions',
    'nonBreakingActions',
  ];

  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder) {
    this.stateForm = this.fb.group({
      startState: ['', Validators.required],
      event: ['', Validators.required],
      endStateType: ['', Validators.required],
      actionType: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.initializeAvailableStates();
    this.setupFormSubscriptions();

    if (this.initialState) {
      this.setInitialState();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setInitialState() {
    // Update available options first
    this.updateAvailableEvents(this.initialState!.startState);
    this.updateAvailableEndStates(
      this.initialState!.startState,
      this.initialState!.event
    );

    // Then set the form values
    this.stateForm.patchValue({
      startState: this.initialState!.startState,
      event: this.initialState!.event,
      endStateType: this.initialState!.endStateType,
      actionType: this.initialState!.actionType,
    });

    // Emit initial state if valid
    if (this.stateForm.valid) {
      this.emitSelectedState();
    }
  }

  private hasEndStates(step: any): boolean {
    return !!(step.endStateOne || step.endStateTwo);
  }

  private initializeAvailableStates() {
    this.availableStartStates = [
      ...new Set(
        this.workflowConfig.workflow
          .filter(step => this.hasEndStates(step))
          .map(step => step.startState)
      ),
    ].sort();
  }

  private setupFormSubscriptions() {
    // Monitor overall form validity and value changes
    this.stateForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        )
      )
      .subscribe(() => {
        if (this.stateForm.valid) {
          this.emitSelectedState();
        }
      });

    // Update available events when start state changes
    this.stateForm
      .get('startState')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(startState => {
        if (startState) {
          this.updateAvailableEvents(startState);
          // Clear dependent fields
          this.stateForm.patchValue(
            {
              event: '',
              endStateType: '',
              actionType: '',
            },
            { emitEvent: true }
          );
        } else {
          this.availableEvents = [];
          this.availableEndStates = [];
          this.stateForm.patchValue(
            {
              event: '',
              endStateType: '',
              actionType: '',
            },
            { emitEvent: true }
          );
        }
      });

    // Update available end states when event changes
    this.stateForm
      .get('event')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event) {
          const startState = this.stateForm.get('startState')?.value;
          this.updateAvailableEndStates(startState, event);
          // Clear end state
          this.stateForm.patchValue(
            {
              endStateType: '',
              actionType: '',
            },
            { emitEvent: true }
          );
        } else {
          this.availableEndStates = [];
          this.stateForm.patchValue(
            {
              endStateType: '',
              actionType: '',
            },
            { emitEvent: true }
          );
        }
      });
  }

  private updateAvailableEvents(startState: string) {
    this.availableEvents = this.workflowConfig.workflow
      .filter(step => step.startState === startState && this.hasEndStates(step))
      .map(step => step.event)
      .sort();
  }

  private updateAvailableEndStates(startState: string, event: string) {
    const step = this.workflowConfig.workflow.find(
      s => s.startState === startState && s.event === event
    );

    this.availableEndStates = [];

    if (step?.endStateOne) {
      this.availableEndStates.push({
        type: 'endStateOne',
        code: step.endStateOne.stateCode,
      });
    }

    if (step?.endStateTwo) {
      this.availableEndStates.push({
        type: 'endStateTwo',
        code: step.endStateTwo.stateCode,
      });
    }

    this.availableEndStates.sort((a, b) => a.code.localeCompare(b.code));
  }

  private emitSelectedState() {
    const formValue = this.stateForm.value;
    const step = this.workflowConfig.workflow.find(
      s => s.startState === formValue.startState && s.event === formValue.event
    );

    if (step) {
      const selectedEndState = step[formValue.endStateType];

      if (selectedEndState) {
        const state: WorkflowState = {
          startState: formValue.startState,
          event: formValue.event,
          endStateType: formValue.endStateType,
          endStateCode: selectedEndState.stateCode,
          actionType: formValue.actionType,
          displayName: step.eventNames,
        };

        this.stateSelected.emit(state);
      }
    }
  }

  getSelectedEndStateCode(): string {
    if (!this.stateForm.valid) return '';

    const endState = this.availableEndStates.find(
      state => state.type === this.stateForm.get('endStateType')?.value
    );

    return endState?.code || '';
  }

  getActionTypeLabel(actionType: ActionType): string {
    switch (actionType) {
      case 'breakingAction':
        return 'Breaking Action';
      case 'breakingActions':
        return 'Breaking Actions';
      case 'nonBreakingActions':
        return 'Non-Breaking Actions';
      default:
        return actionType;
    }
  }
}
